// Projects > Travel lens. URL-owned state:
//   /projects/travel?view=month|list&month=YYYY-MM&tz=…&travelProject&travelPerson&travelMarket&travelPurpose
// Real data only (GET /api/project-travel). Project links open the existing
// guarded record drawer through onOpenProject (travel query preserved).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "../../../app/tokens.js";
import { PURPOSES, civilDate } from "../../../lib/travelTime.js";
import { getTravelOptions, getTravelMonth } from "../../../data/projectTravelClient.js";
import { tx } from "./travelStyles.js";
import { showToast } from "../../../components/toastService.jsx";
import { TravelKpis, TRAVEL_METRICS } from "./TravelKpis.jsx";
import { TravelKey } from "./TravelKey.jsx";
import { TravelTimezone } from "./TravelTimezone.jsx";
import "./travelToolbar.css";
import {
  readTravelUrl, patchTravelSearch, currentMonth, shiftMonth, zoneChoices, fmtCivil,
  FILTER_PARAMS,
} from "./travelModel.js";
import { useTravelTimezone } from "./useTravelTimezone.js";
import { TravelMonth } from "./TravelMonth.jsx";
import { TravelList } from "./TravelList.jsx";
import { TravelSignals } from "./TravelSignals.jsx";
import { TripDetailModal } from "./TripDetailModal.jsx";
import { TripEditorModal } from "./TripEditorModal.jsx";

function useNow() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    let interval = null;
    const timeout = setTimeout(() => { setNow(Date.now()); interval = setInterval(() => setNow(Date.now()), 60000); }, 60000 - (Date.now() % 60000));
    return () => { clearTimeout(timeout); if (interval) clearInterval(interval); };
  }, []);
  return now;
}

function StateCard({ testId, title, body, action, role = "status" }) {
  return (
    <div data-testid={testId} role={role} style={{ ...tx.card, padding: "36px 28px", textAlign: "center" }}>
      <div style={{ fontSize: T.fsMd, fontWeight: T.fwBold, color: T.projectsHubNavy, marginBottom: 6 }}>{title}</div>
      {body && <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: action ? 14 : 0, maxWidth: 520, margin: "0 auto" }}>{body}</div>}
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}

function GridSkeleton() {
  return (
    <div data-testid="travel-loading" aria-busy="true" style={{ ...tx.card, overflow: "hidden" }}>
      <div style={{ height: 30, background: T.travelDowBg }} />
      {[0, 1, 2, 3, 4].map((w) => (
        <div key={w} style={{ height: 112, borderTop: `1px solid ${T.travelHair}`, padding: "32px 10px 0" }}>
          <div style={{ width: `${30 + ((w * 17) % 45)}%`, marginLeft: `${(w * 13) % 40}%`, height: 20, borderRadius: 6, background: T.lightGray }} />
        </div>
      ))}
    </div>
  );
}

export function ProjectsTravel({ authIdentity = "unbound", onOpenProject }) {
  const location = useLocation();
  const navigate = useNavigate();
  const url = useMemo(() => readTravelUrl(location.search), [location.search]);
  const setUrl = useCallback((patch, replace = false) => {
    const search = patchTravelSearch(location.search, patch);
    if (search !== location.search) navigate(`${location.pathname}${search}`, { replace, state: location.state });
  }, [location.pathname, location.search, location.state, navigate]);

  const tzState = useTravelTimezone(authIdentity, url.tz, { onUrlChange: (tz) => setUrl({ tz }) });
  const zone = url.invalidTimezone ? null : tzState.zone;
  const now = useNow();
  const month = url.month || (zone ? currentMonth(zone, now) : null);
  const today = zone ? civilDate(now, zone) : null;

  // Options (filters, pickers, zones) — once per identity.
  const [opts, setOpts] = useState({ status: "loading", data: null, error: null });
  const [optsRetry, setOptsRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setOpts({ status: "loading", data: null, error: null });
    Promise.resolve().then(() => getTravelOptions())
      .then((data) => { if (active) setOpts({ status: "ok", data, error: null }); })
      .catch((error) => { if (active) setOpts({ status: "error", data: null, error }); });
    return () => { active = false; };
  }, [authIdentity, optsRetry]);
  const options = opts.data;

  // Month data. A failed refresh keeps the last good dataset for this identity.
  const [data, setData] = useState({ key: null, status: "loading", payload: null, error: null });
  const [refresh, setRefresh] = useState(0);
  const filterKey = JSON.stringify(url.filters);
  const queryKey = zone && month ? `${authIdentity}|${month}|${zone}|${filterKey}` : null;
  useEffect(() => {
    if (!queryKey) return undefined;
    let active = true;
    setData((d) => ({ key: queryKey, status: "loading", payload: d.payload && d.key?.startsWith(`${authIdentity}|`) ? d.payload : null, error: null }));
    Promise.resolve().then(() => getTravelMonth({ month, tz: zone, filters: url.filters }))
      .then((payload) => { if (active) setData({ key: queryKey, status: "ok", payload, error: null }); })
      .catch((error) => { if (active) setData((d) => ({ key: queryKey, status: "error", payload: d.payload, error })); });
    return () => { active = false; };
  }, [queryKey, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = url.invalidTimezone ? null : data.payload;
  const stale = data.status === "loading" && payload;
  const showNames = payload?.privacy?.showTravelerNames ?? options?.privacy?.showTravelerNames ?? true;
  const canCreate = Boolean(payload?.capabilities?.canCreate ?? options?.capabilities?.canCreate);
  const trips = useMemo(() => payload?.trips || [], [payload]);
  // Local tile state never enters the URL/API query, so totals cannot collapse
  // into their own filtered result and ordinary URL facets keep working.
  const [activeMetric, setActiveMetric] = useState(null);
  const metricScope = `${authIdentity}|${month}|${zone}|${filterKey}`;
  const [metricScopeAtActivation, setMetricScopeAtActivation] = useState(null);
  const metric = metricScopeAtActivation === metricScope ? activeMetric : null;
  const activateMetric = (key) => { setActiveMetric(key); setMetricScopeAtActivation(metricScope); };
  const displayTrips = useMemo(() => {
    const field = { trips: "tripIds", people: "peopleTripIds", projects: "projectTripIds" }[metric];
    if (!field) return trips;
    const ids = new Set(payload?.monthly?.[field] || []);
    return trips.filter(t => ids.has(t.id));
  }, [metric, payload, trips]);
  const holidays = payload?.holidays || [];
  const signals = payload?.signals;
  const holidayTripIds = useMemo(() => new Set((signals?.holidayOverlaps || []).map((h) => h.tripId)), [signals]);
  const gapTripIds = useMemo(() => new Set(trips.filter((t) => t.milestoneId && !t.travelerCount).map((t) => t.id)), [trips]);

  const [detailId, setDetailId] = useState(null);
  const [editor, setEditor] = useState({ open: false, trip: null });
  const [filtersOpen, setFiltersOpen] = useState(Object.keys(url.filters).length > 0);

  const openProject = useCallback((projectId, tab = "overview") => {
    if (!projectId) return;
    setDetailId(null);
    if (tab === "overview") onOpenProject?.(projectId);
    else onOpenProject?.(projectId, undefined, tab);
  }, [onOpenProject]);
  const choices = useMemo(() => zoneChoices(options, tzState.choice, now), [options, tzState.choice, now]);
  const activeFilters = Object.entries(url.filters);
  const filterOptions = {
    travelProject: (options?.projects || []).map((p) => [p.id, p.name]),
    travelPerson: (options?.people || []).map((p) => [p.personId, p.displayName]),
    travelMarket: (options?.markets || []).map((m) => [m.id, m.name]),
    travelPurpose: Object.entries(PURPOSES),
  };
  const filterName = (key, value) => filterOptions[key].find(([v]) => v === value)?.[1] || "Unavailable selection";

  const monthLabel = month ? fmtCivil(`${month}-01`, { month: "long", year: "numeric" }) : "";
  const zoneLoading = !zone && !tzState.localUnavailable && !url.invalidTimezone;

  return (
    <section data-testid="projects-travel" aria-label="Travel" style={{ paddingBottom: 40 }}>
      <TravelKpis monthly={payload?.monthly} signals={signals} active={metric} onActivate={activateMetric} loading={!payload || Boolean(stale)} />
      {/* Toolbar */}
      <div data-testid="travel-toolbar" className="travel-toolbar" style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", marginBottom: 12 }}>
        <button type="button" aria-label="Previous month" data-testid="travel-prev" style={tx.box} disabled={!month} onClick={() => setUrl({ month: shiftMonth(month, -1) })}>‹</button>
        <h2 data-testid="travel-month-label" aria-live="polite" style={{ fontSize: T.fsBase, fontWeight: T.fwBold, margin: 0, color: T.dark, whiteSpace: "nowrap", textAlign: "center", minWidth: "max-content", flexShrink: 0 }}>{monthLabel || "\u00a0"}</h2>
        <button type="button" aria-label="Next month" data-testid="travel-next" style={tx.box} disabled={!month} onClick={() => setUrl({ month: shiftMonth(month, 1) })}>›</button>
        <button type="button" data-testid="travel-today" style={{ ...tx.box, borderColor: "var(--v2-line)", color: "var(--v2-mid)" }} disabled={!zone} onClick={() => setUrl({ month: currentMonth(zone, Date.now()) })}>Today</button>
        <span role="radiogroup" aria-label="View" style={{ display: "inline-flex", background: T.travelSegBg, borderRadius: 9, padding: 3 }}>
          {[["month", "Month"], ["list", "List"]].map(([v, l]) => (
            <button key={v} type="button" role="radio" aria-checked={url.view === v} data-testid={`travel-view-${v}`} onClick={() => setUrl({ view: v === "month" ? null : v })}
              style={{ border: 0, padding: "5px 12px", borderRadius: 7, fontSize: T.fsBase, fontWeight: T.fwSemibold, cursor: "pointer", fontFamily: "inherit", background: url.view === v ? T.card : "transparent", color: url.view === v ? T.dark : T.tabInactiveFg, boxShadow: url.view === v ? T.travelSegShadow : "none" }}>{l}</button>
          ))}
        </span>
        <button type="button" data-testid="travel-filter-toggle" aria-expanded={filtersOpen}
          style={{ ...tx.box, gap: 7, padding: "8px 13px", fontSize: T.fsSm5, borderColor: activeFilters.length ? "var(--v2-blue-border)" : "var(--v2-line)", background: activeFilters.length ? "var(--v2-blue-tint)" : "var(--v2-card)", color: activeFilters.length ? "var(--v2-accent)" : "var(--v2-mid)", fontWeight: activeFilters.length ? T.fwBold : T.fwSemibold }}
          onClick={() => setFiltersOpen((o) => !o)}>
          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
          Filter
          {activeFilters.length > 0 && <span data-testid="travel-filter-count" style={{ background: "var(--v2-accent)", color: T.white, borderRadius: 999, fontSize: T.fsXxs, fontWeight: T.fwBold, padding: "1px 6px", minWidth: 18, textAlign: "center" }}>{activeFilters.length}</span>}
        </button>
        <span style={{ flex: 1, minWidth: 0 }} />
        <TravelTimezone key={authIdentity} choices={choices} choice={tzState.choice} zone={zone} now={now} loading={zoneLoading} onSelect={tzState.select} />
        <TravelKey coverage={payload?.holidayCoverage} countries={payload?.holidayCountries} />
        {canCreate && <button type="button" data-testid="travel-add" style={tx.primary} onClick={() => setEditor({ open: true, trip: null })}>+ Add trip</button>}
      </div>

      {/* Preference + notices */}
      {tzState.prefStatus === "error" && !url.tz && <div role="status" data-testid="travel-pref-error" style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 8 }}>Your saved time zone couldn't be loaded; showing Local.</div>}
      {tzState.saveState.status === "error" && <div role="alert" data-testid="travel-pref-save-error" style={{ fontSize: T.fsXs, color: T.red, marginBottom: 8 }}>Time zone applied but not saved for next time: {tzState.saveState.error}</div>}
      {tzState.source === "url" && tzState.prefStatus === "ready" && <div data-testid="travel-tz-url-note" style={{ fontSize: T.fsXs, color: T.mid, marginBottom: 8 }}>Time zone from this link. Your saved choice is unchanged until you pick one.</div>}

      {/* Filters */}
      {filtersOpen && (
        <div data-testid="travel-filters" style={{ ...tx.card, padding: "10px 12px", marginBottom: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          {opts.status === "error" && <span role="alert" style={tx.err}>Filter choices unavailable: {opts.error?.message} <button type="button" style={tx.link} onClick={() => setOptsRetry((n) => n + 1)}>Retry</button></span>}
          {Object.entries(FILTER_PARAMS).map(([key, label]) => (
            <label key={key} style={{ minWidth: 170, flex: "1 1 170px" }}>
              <span style={tx.label}>{label}</span>
              <select data-testid={`travel-filter-${key}`} style={tx.input} value={url.filters[key] || ""} disabled={opts.status === "loading"} onChange={(e) => setUrl({ [key]: e.target.value || null })}>
                <option value="">All</option>
                {url.filters[key] && !filterOptions[key].some(([v]) => v === url.filters[key]) && <option value={url.filters[key]}>Unavailable selection</option>}
                {filterOptions[key].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          ))}
          {activeFilters.length > 0 && <button type="button" data-testid="travel-filter-clear" style={tx.ghost} onClick={() => setUrl(Object.fromEntries(Object.keys(FILTER_PARAMS).map((k) => [k, null])))}>Clear</button>}
        </div>
      )}
      {!filtersOpen && activeFilters.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
          {activeFilters.map(([k, v]) => (
            <button key={k} type="button" data-testid={`travel-chip-${k}`} onClick={() => setUrl({ [k]: null })} style={{ ...tx.ghost, borderRadius: 999, padding: "3px 10px", fontSize: T.fsXs }}>
              {FILTER_PARAMS[k]}: {filterName(k, v)} <span aria-label="Remove filter">×</span>
            </button>
          ))}
        </div>
      )}

      {metric && <div style={{ fontSize: T.fsSm, marginBottom: 10 }}>
        {TRAVEL_METRICS.find(m => m.key === metric)?.label} active{" "}
        <button type="button" style={tx.link} data-testid="travel-metric-reset" onClick={() => activateMetric(null)}>Reset tile</button>
      </div>}
      {payload && <TravelSignals open={metric} signals={signals} showNames={showNames} onOpenTrip={setDetailId} onOpenProject={openProject} />}

      {data.status === "error" && payload && (
        <div role="alert" data-testid="travel-refresh-error" style={{ fontSize: T.fsSm, color: T.red, marginBottom: 8 }}>
          Couldn't refresh: {data.error?.message}. Showing the last loaded view. <button type="button" style={tx.link} onClick={() => setRefresh((n) => n + 1)}>Retry</button>
        </div>
      )}

      <div style={{ opacity: stale ? 0.6 : 1, transition: "opacity .15s" }} aria-busy={stale || undefined}>
        {tzState.localUnavailable && (
          <StateCard testId="travel-local-unavailable" title="Your browser's time zone is unavailable"
            body="Times can't be shown in Local. Choose UTC or a specific zone in Times in."
            action={<button type="button" data-testid="travel-use-utc" style={tx.ghost} onClick={() => tzState.select("UTC")}>Show times in UTC</button>} />
        )}
        {(zoneLoading || (!payload && data.status === "loading")) && <GridSkeleton />}
        {!payload && data.status === "error" && (
          data.error?.status === 401 || data.error?.status === 403
            ? <StateCard testId="travel-denied" title="You don't have access to Travel" body={data.error?.message || "Ask a PMO admin if you need to see team travel."} />
            : <StateCard testId="travel-error" role="alert" title="Couldn't load travel" body={data.error?.message || "The request failed."}
                action={<button type="button" data-testid="travel-retry" style={tx.ghost} onClick={() => setRefresh((n) => n + 1)}>Retry</button>} />
        )}
        {payload && url.view === "month" && (
          <TravelMonth month={month} zone={zone} today={today} trips={displayTrips} holidays={holidays} showNames={showNames}
            holidayTripIds={holidayTripIds} gapTripIds={gapTripIds} onOpenTrip={setDetailId} />
        )}
        {payload && url.view === "list" && (displayTrips.length
          ? <TravelList trips={displayTrips} zone={zone} showNames={showNames} holidayTripIds={holidayTripIds} onOpenTrip={setDetailId} onOpenProject={openProject} />
          : <StateCard testId="travel-empty" title={metric ? "No trips match this tile and these filters" : activeFilters.length ? "No trips match these filters" : `No trips in ${monthLabel}`}
              body={activeFilters.length ? "Clear a filter or move to another month." : canCreate ? "Add the first trip for this month." : "Nothing is scheduled in this view."}
              action={activeFilters.length
                ? <button type="button" style={tx.ghost} onClick={() => setUrl(Object.fromEntries(Object.keys(FILTER_PARAMS).map((k) => [k, null])))}>Clear filters</button>
                : canCreate ? <button type="button" style={tx.primary} onClick={() => setEditor({ open: true, trip: null })}>+ Add trip</button> : null} />)}
        {payload && url.view === "month" && displayTrips.length === 0 && (
          <div data-testid="travel-month-empty" style={{ fontSize: T.fsSm, color: T.mid, marginTop: 8 }}>{metric ? "No trips match this tile and these filters. Reset the tile or change filters." : activeFilters.length ? "No trips match these filters in this month." : "No trips in this month yet."}</div>
        )}
      </div>

      <TripDetailModal tripId={detailId} zone={zone || "UTC"} showNames={showNames} onClose={() => setDetailId(null)} onOpenProject={openProject}
        onEdit={(trip) => { setDetailId(null); setEditor({ open: true, trip }); }}
        onDeleted={() => { setDetailId(null); showToast("Trip deleted", { type: "success" }); setRefresh((n) => n + 1); }} />
      {opts.status === "ok" && (
        <TripEditorModal open={editor.open} trip={editor.trip} options={options} defaultProjectId={url.filters.travelProject || null}
          onClose={() => setEditor({ open: false, trip: null })}
          onSaved={(saved) => { const wasEdit = Boolean(editor.trip); setEditor({ open: false, trip: null }); showToast(wasEdit ? "Trip updated" : "Trip added", { type: "success" }); setRefresh((n) => n + 1); if (saved?.id) setDetailId(saved.id); }} />
      )}
      {url.invalidTimezone && <StateCard testId="travel-invalid-timezone" role="alert" title="This link's time zone is unavailable"
        body="Choose an available zone in Times in. Unknown zones are never assumed to be UTC."
        action={<button type="button" style={tx.ghost} onClick={()=>tzState.select("UTC")}>Show times in UTC</button>} />}
      {editor.open && opts.status !== "ok" && (
        <div role="alert" style={tx.err}>Trip choices are unavailable right now. <button type="button" style={tx.link} onClick={() => setOptsRetry((n) => n + 1)}>Retry</button></div>
      )}
    </section>
  );
}

export default ProjectsTravel;
